/**
 * LessonView — Student-side renderer of a teacher-published lesson.
 *
 * Mirrors the Teacher's Content Editor Preview (see
 * `components/teacher/ContentEditor/ContentEditor.jsx`, the `Preview` block
 * starting ~line 2143) so what the teacher previewed is what the student
 * sees: hero, sections by type, subsections with their own charts/drawings,
 * section attachments, reference links (incl. YouTube), tables, equations
 * — the works.
 *
 * Visual differences vs. teacher preview:
 *   • Uses student-theme CSS variables (`var(--st-*)`) instead of a
 *     PREVIEW_THEME palette, so the page respects dark/light mode AND the
 *     student's chosen accent.
 *   • Attachments go through the student-side download endpoint (enrollment-
 *     gated, not owner-gated) via `StudentAttachmentPreviewCard`.
 *   • Image lightbox + zoom level are kept from the previous LessonView API.
 *
 * The renderer is intentionally self-contained — every section variant is a
 * small inline subcomponent so the file reads top-down without jumping to
 * helpers in other modules.
 */
import React, { useMemo, useState } from 'react';
import { AccentRule } from '../../studentUi';
import { createPortal } from 'react-dom';
import {
  X, ZoomIn, BarChart3, Paperclip, Globe, PlayCircle, CheckCircle, BookOpen,
  Link2, Sparkles, GraduationCap, ChevronDown, Loader2,
} from 'lucide-react';

import { RenderContent, MathText } from '../../../../components/RenderContent';
import PreviewLightbox from '../../../../components/PreviewLightbox';
import { isDataUrl, isViewableInlineImage, isOpenableLink } from '../../../../lib/referenceUrl';
import { ChartPreviewBody, DrawingPreviewBody } from '../../../../components/teacher/ContentEditor/PreviewBodies';
import SectionEquations from '../../../../components/teacher/SectionEquations';
import { getSectionIcon } from '../../../../lib/sectionIcons';
import { StudentAttachmentPreviewCard, StudentAttachmentViewerModal } from './StudentAttachmentCards';
import api from '../../studentApi';
import { getExamPrepColors } from '../../examPrepTheme';
import { useStudent } from '../../StudentApp';
import { firstPublishedAt, heroTopics } from './lessonHeaderInfo';
import { toSafeHref } from '../../../../lib/resourceLink';
import Loader from '../../../../components/Loader';

const DIFF = {
  easy:   { bg: '#DCFCE7', text: '#166534', l: 'Easy' },
  medium: { bg: '#FEF9C3', text: '#854D0E', l: 'Medium' },
  hard:   { bg: '#FEE2E2', text: '#991B1B', l: 'Hard' },
};

const TABLE_DEFAULT_COLORS = {
  header_bg: '#0d9488', header_text: '#ffffff', accent: '#0d9488',
  odd_row_bg: '#ffffff', even_row_bg: '#f0fdfa', text: '#1e293b', border: '#0d9488',
};
const resolveTableColors = (table) => ({ ...TABLE_DEFAULT_COLORS, ...(table?.colors || {}) });

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
};

// Show "Created at" (the go-live moment students first saw the session) unless
// the teacher later republished it — then "Updated at" with the republish time.
// created_at = published_lessons.published_at (stable go-live / scheduled time);
// updated_at moves only on a real republish, so updated > created ⟺ republished.
const lessonStamp = (lesson) => {
  const created = lesson?.created_at || lesson?.published_at || '';
  const updated = lesson?.updated_at || '';
  if (updated && created && updated > created) return { label: 'Updated at', iso: updated };
  return { label: 'Created at', iso: created };
};

// Pull the student's primary accent out of the page's theme variable so
// every section uses a single, consistent highlight colour. Falls back to
// the brand green if the variable hasn't resolved yet (SSR-style first paint).
// The theme sets its variables on the student shell, not on <html>, so read them there.
// `name` is a readable-as-text token (themeToCssVars guarantees 4.5:1 on the surfaces).
function useThemeColor(name, fallback) {
  return useMemo(() => {
    try {
      const host = document.querySelector('[data-testid="student-shell"]') || document.documentElement;
      return getComputedStyle(host).getPropertyValue(name).trim() || fallback;
    } catch { return fallback; }
  }, [name, fallback]);
}
const useAccent = () => useThemeColor('--st-primary-ink', '#1A7F37');
// Second theme colour, for takeaways, hints and board extracts.
const useAccent2 = () => useThemeColor('--st-accent-ink', '#0F766E');


export default function LessonView({ lesson, zoom = 1 }) {
  const [lightbox, setLightbox] = useState(null);   // { src }
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const accent = useAccent();

  const framework = lesson.framework || {};
  const sections = lesson.framework_sections || framework.sections || [];
  const validSections = sections.filter(s => s && (s.type || s.content || s.items || s.charts || s.section_images || s.generated_images));
  const totalValid = validSections.length;
  // Inline `[ref:N]` markers in any section's prose resolve against this
  // list (carried from `draft.references` at publish time).
  const references = lesson.references || [];

  // No framework — fall back to plain content / transcript rendering
  if (!validSections.length) {
    return (
      <article className="max-w-3xl mx-auto px-6 sm:px-10 py-8" data-testid="lesson-view" style={{ zoom, lineHeight: 1.65 }}>
        <LessonHeader lesson={lesson} totalValid={0} accent={accent} />
        <LessonFallback content={lesson.content || lesson.transcript} />
        <LessonReferences references={references} accent={accent} />
        <SuggestedExamQuestions publishedLessonId={lesson.id} />
      </article>
    );
  }

  return (
    <article className="pb-20" data-testid="lesson-view" style={{ zoom, lineHeight: 1.65 }}>
      {/* Hero — same composition as the teacher preview's hero. */}
      <Hero lesson={lesson} totalValid={totalValid} accent={accent} />

      {/* Sections */}
      <div className="max-w-3xl mx-auto px-6 sm:px-10 pt-10 space-y-14">
        {validSections.map((sec, idx) => (
          <SectionBlock
            key={sec.id || idx}
            sec={sec}
            idx={idx}
            total={totalValid}
            accent={accent}
            references={references}
            onExpand={setLightbox}
            onOpenAttachment={setPreviewAttachment}
          />
        ))}

        <LessonReferences references={references} accent={accent} />
        <SuggestedExamQuestions publishedLessonId={lesson.id} />
      </div>

      {lightbox && <Lightbox src={lightbox.src} onClose={() => setLightbox(null)} />}
      {previewAttachment && (
        <StudentAttachmentViewerModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
      )}
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Header (used in the fallback path)
// ═══════════════════════════════════════════════════════════════════════════
function LessonHeader({ lesson, accent }) {
  return (
    <header className="mb-6">
      {firstPublishedAt(lesson) && (
        <div className="text-xs font-semibold" style={{ color: 'var(--st-mute)' }} data-testid="lesson-first-published">
          {fmtDate(firstPublishedAt(lesson))}
        </div>
      )}
      <h1 className="font-semibold leading-tight mt-2 text-3xl sm:text-4xl" style={{ fontFamily: 'var(--st-font-heading)' }}>
        {lesson.title}
      </h1>
      {lesson.teacher_name && (
        <div className="mt-2 text-sm" style={{ color: 'var(--st-mute)' }}>Taught by {lesson.teacher_name}</div>
      )}
      {(() => { const s = lessonStamp(lesson); return s.iso ? <div className="mt-1 text-sm" style={{ color: 'var(--st-mute)' }}>{s.label} {fmtDateTime(s.iso)}</div> : null; })()}
      {lesson.key_topics?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {lesson.key_topics.map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={{ borderColor: `${accent}25`, background: `${accent}10`, color: accent }}>{t}</span>
          ))}
        </div>
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Hero — title, tags, description, progress hint
// ═══════════════════════════════════════════════════════════════════════════
function Hero({ lesson, totalValid, accent }) {
  return (
    <div className="relative overflow-hidden border-b" style={{ borderColor: 'var(--st-border)' }}>
      {/* Tinted background — uses the student's accent so the hero feels
          designed, not generic. Falls back gracefully when accent isn't set. */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(circle at 25% 70%, ${accent}22, transparent 45%), radial-gradient(circle at 75% 25%, ${accent}12, transparent 40%)`,
      }} />
      <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pt-10 pb-10">
        {/* Date the session first went live — never the republish time. */}
        {firstPublishedAt(lesson) && (
          <div className="text-xs font-semibold mb-4" style={{ color: 'var(--st-mute)' }} data-testid="lesson-first-published">
            {fmtDate(firstPublishedAt(lesson))}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.12] mb-4" style={{ fontFamily: 'var(--st-font-heading)' }}>
          {lesson.title}
        </h1>
        <div className="mt-5 flex items-center gap-4 text-xs" style={{ color: 'var(--st-mute)' }}>
          {lesson.teacher_name && <span>Taught by <span className="font-semibold" style={{ color: 'var(--st-ink)' }}>{lesson.teacher_name}</span></span>}
          {(() => { const s = lessonStamp(lesson); return s.iso ? <span>{s.label} <span className="font-semibold" style={{ color: 'var(--st-ink)' }}>{fmtDateTime(s.iso)}</span></span> : null; })()}
          {(() => {
            const { shown, hasMore } = heroTopics(lesson.key_topics, totalValid);
            if (shown.length === 0) return null;
            const chip = 'px-2 py-0.5 rounded border text-xs font-semibold';
            const chipStyle = { borderColor: 'var(--st-border)', background: 'var(--st-card)', color: 'var(--st-ink)' };
            return (
              <span className="inline-flex flex-wrap gap-1.5" data-testid="lesson-topic-chips">
                {shown.map(t => <span key={t} className={chip} style={chipStyle}>{t}</span>)}
                {/* More sections than chips — the full list is in the lesson below. */}
                {hasMore && <span className={chip} style={chipStyle} aria-label="More sections" data-testid="lesson-topic-more">…</span>}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Section — dispatcher by type. Each variant is a small inline component.
// ═══════════════════════════════════════════════════════════════════════════
function SectionBlock({ sec, idx, total, accent, references, onExpand, onOpenAttachment }) {
  // Same icon the teacher sees on this section in the Content Editor — the
  // student badge used to be a bare number, which carried no meaning the
  // "Section 01 / 08" caption below it didn't already say.
  const SecIcon = getSectionIcon(sec.icon, sec.title);
  return (
    <section id={`student-pv-sec-${sec.id || idx}`} data-testid={`lesson-section-${sec.id || idx}`}>
      {/* Section header */}
      <div className="flex items-start gap-4 mb-7">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}14`, color: accent }}
          data-testid={`lesson-section-icon-${sec.id || idx}`}>
          <SecIcon className="w-[22px] h-[22px]" />
        </div>
        <div className="pt-0.5">
          <p className="text-xs font-semibold tabular-nums mb-1" style={{ color: accent }}>
            Section {String(idx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </p>
          <h2 className="text-2xl font-semibold leading-snug" style={{ fontFamily: 'var(--st-font-heading)' }}>{sec.title}</h2>
          <AccentRule className="mt-2.5" />
        </div>
      </div>

      <div className="space-y-6">
        {(sec.type === 'text' || sec.type === 'rich_text' || !sec.type) && (
          <TextSectionBody sec={sec} accent={accent} references={references} onExpand={onExpand} />
        )}
        {sec.type === 'cards' && <CardsSection sec={sec} accent={accent} />}
        {sec.type === 'visual' && <VisualSection sec={sec} onExpand={onExpand} />}
        {sec.type === 'flashcards' && <FlashcardsSection sec={sec} accent={accent} />}
        {sec.type === 'problems' && <ProblemsSection sec={sec} accent={accent} />}
        {sec.type === 'list' && <ListSection sec={sec} accent={accent} />}
        {sec.type === 'blackboard_analysis' && <BlackboardSection sec={sec} accent={accent} />}

        {/* Structured equations — render across all section types */}
        {Array.isArray(sec.equations) && sec.equations.length > 0 && (
          <SectionEquations equations={sec.equations} />
        )}

        {/* Section-level images (drawings + uploaded photos) — always at
            the section level no matter the section type. */}
        <SectionImages sec={sec} onExpand={onExpand} accent={accent} />

        {/* Section-level reference links (URL + YouTube) */}
        <SectionLinks sec={sec} accent={accent} />

        {/* Section-level charts (DeskeyChart) — visible across all types */}
        <SectionCharts sec={sec} accent={accent} />

        {/* Section-level attachments */}
        <SectionAttachments sec={sec} accent={accent} onOpen={onOpenAttachment} />
      </div>
    </section>
  );
}

// ─── Text / Rich-Text section ─────────────────────────────────────────────
function TextSectionBody({ sec, accent, references, onExpand }) {
  const accent2 = useAccent2();
  return (
    <>
      {sec.content && (
        <RenderContent text={sec.content} highlights={sec.highlights} references={references}
          className="text-base leading-[1.95]" />
      )}
      {Array.isArray(sec.objectives) && sec.objectives.length > 0 && (
        <div className="rounded-2xl p-5 border relative overflow-hidden"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 w-1 h-full rounded-r" style={{ background: accent }} />
          <p className="text-xs font-semibold mb-3 ml-4" style={{ color: accent }}>Learning objectives</p>
          <div className="ml-4 space-y-2.5">
            {sec.objectives.map((o, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                <span className="text-[15px] leading-relaxed"><MathText text={o} /></span>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(sec.takeaways) && sec.takeaways.length > 0 && (
        <div className="rounded-2xl p-5 border relative overflow-hidden"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 w-1 h-full rounded-r" style={{ background: accent2 }} />
          <p className="text-xs font-semibold mb-3 ml-4" style={{ color: accent2 }}>Key takeaways</p>
          <div className="ml-4 space-y-2.5">
            {sec.takeaways.map((t, i) => (
              <p key={i} className="text-[15px] leading-relaxed">
                <span className="font-semibold mr-2 tabular-nums" style={{ color: accent2, fontFamily: 'var(--st-font-heading)' }}>{String(i + 1).padStart(2, '0')}</span>
                <MathText text={t} />
              </p>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(sec.subsections) && sec.subsections.map((sub, i) => (
        <SubsectionBlock key={i} sub={sub} parentHighlights={sec.highlights} references={references} accent={accent} sectionId={sec.id || `sub-${i}`} onExpand={onExpand} />
      ))}
      {Array.isArray(sec.tables) && sec.tables.length > 0 && sec.tables.map((t, i) => <LessonTable key={i} table={t} sectionId={sec.id} idx={i} />)}
    </>
  );
}

function SubsectionBlock({ sub, parentHighlights, references, accent, sectionId, onExpand }) {
  return (
    <div className="border-l-2 pl-5 py-1" style={{ borderColor: `${accent}30` }}>
      {sub.title && (
        <p className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--st-font-heading)' }}>{sub.title}</p>
      )}
      {sub.content && (
        <RenderContent text={sub.content} highlights={sub.highlights || parentHighlights} references={references}
          className="text-base leading-[1.9]" />
      )}
      {/* Subsection-level charts — same fallback-safe renderer used at
          the section level so a chart inside a subtitle never goes blank. */}
      {(sub.charts || []).length > 0 && (
        <div className="mt-3 space-y-3" data-testid={`student-sub-charts-${sectionId}`}>
          {sub.charts.map((ch, ci) => (
            <div key={ch.id || ci}
              className="rounded-2xl border overflow-hidden shadow-sm"
              style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
              data-testid={`student-sub-chart-${sectionId}-${ci}`}>
              <div className="px-4 py-2 border-b flex items-center gap-2" style={{ background: `${accent}10`, borderColor: `${accent}22` }}>
                <BarChart3 className="w-3.5 h-3.5" style={{ color: accent }} />
                <p className="text-xs font-semibold">{ch.title || `Flowchart ${ci + 1}`}</p>
                <span className="text-xs font-mono px-1.5 py-0.5 rounded ml-auto" style={{ background: `${accent}14`, color: accent }}>
                  {(ch.nodes || []).length} shapes · {(ch.connectors || []).length} lines
                </span>
              </div>
              <ChartPreviewBody ch={ch} accent={accent} sBody="" sMuted="" isDark={false} />
            </div>
          ))}
        </div>
      )}
      {(sub.drawings || sub.images || []).filter(im => im?.url).map((im, di) => (
        <div key={di}
          className="mt-3 rounded-2xl border overflow-hidden shadow-sm"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
          data-testid={`student-sub-drawing-${sectionId}-${di}`}>
          <div className="px-4 py-2 border-b flex items-center gap-2" style={{ background: `${accent}10`, borderColor: `${accent}22` }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
            <p className="text-xs font-semibold " style={{ color: 'var(--st-mute)' }}>{im.caption || 'Diagram / Drawing'}</p>
          </div>
          <DrawingPreviewBody url={im.url} caption={im.caption || 'Drawing'} sMuted="" />
        </div>
      ))}
    </div>
  );
}

// ─── Cards / Visual / Flashcards / Problems / List / Blackboard ───────────
function CardsSection({ sec, accent }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3.5">
      {(sec.items || []).map((item, i) => (
        <div key={i} className="rounded-2xl border p-5 relative overflow-hidden hover:shadow-lg transition"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 w-full h-[3px]"
            style={{ background: `linear-gradient(90deg, ${accent}, ${accent}60, transparent)`, opacity: 0.6 }} />
          <p className="text-xs font-semibold mb-2" style={{ color: accent }}>
            {String(i + 1).padStart(2, '0')}
          </p>
          {(item.term || item.title || item.technique) && (
            <p className="text-base font-semibold mb-2"><MathText text={item.term || item.title || item.technique} /></p>
          )}
          {(item.fact || item.definition || item.description || item.content) && (
            <p className="text-sm leading-relaxed" style={{ color: 'var(--st-mute)' }}>
              <MathText text={item.fact || item.definition || item.description || item.content} />
            </p>
          )}
          {item.example && (
            <p className="text-xs mt-3 italic border-t pt-2.5" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>
              e.g., <MathText text={item.example} />
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// Only generated images render here. image_prompts are the AI's suggestions
// for what the teacher COULD generate, not diagrams -- they used to show as
// empty placeholder boxes. The section (header + numbering) still renders;
// it's just empty until the teacher generates or adds something.
function VisualSection({ sec, onExpand }) {
  const imgs = sec.generated_images || [];
  if (!imgs.length) return null;
  return (
    <>
      {imgs.map((img, i) => (
        <button key={i} onClick={() => onExpand({ src: img.url })}
          className="block w-full -mx-6 sm:-mx-10 my-2 relative group"
          data-testid={`student-pv-genimg-${i}`}>
          <img src={img.url} alt="Generated image" className="w-full max-h-[600px] object-contain mx-auto" />
          <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-4 h-4" />
          </span>
        </button>
      ))}
    </>
  );
}

// Flashcards — mirror the teacher editor's flip behaviour (FrameworkSection
// `flashcards`): show the QUESTION first and reveal the ANSWER only on click,
// flipping back and forth. Keeps the student-theme card chrome.
function FlashcardsSection({ sec, accent }) {
  const [flipped, setFlipped] = useState({});
  const toggle = (i) => setFlipped(prev => ({ ...prev, [i]: !prev[i] }));
  return (
    <>
      {(sec.items || []).map((item, i) => {
        const dd = DIFF[item.difficulty] || DIFF.easy;
        const isFlipped = !!flipped[i];
        return (
          <button key={i} type="button" onClick={() => toggle(i)}
            className="w-full text-left rounded-2xl border overflow-hidden transition hover:shadow-md cursor-pointer"
            style={{ borderColor: 'var(--st-border)', background: isFlipped ? `${accent}08` : 'var(--st-card)' }}
            data-testid={`student-flashcard-${i}`} aria-expanded={isFlipped}>
            <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--st-border)' }}>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: dd.bg, color: dd.text }}>{dd.l}</span>
              <span className="text-xs font-semibold " style={{ color: 'var(--st-mute)' }}>Card {i + 1}</span>
              <span className="text-xs font-semibold ml-auto" style={{ color: accent }}>{isFlipped ? 'Answer' : 'Question'}</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-base font-semibold"><MathText text={isFlipped ? item.answer : item.question} /></p>
              <p className="text-xs mt-3" style={{ color: 'var(--st-mute)' }}>
                {isFlipped ? 'Tap to see the question' : 'Tap to reveal the answer'}
              </p>
            </div>
          </button>
        );
      })}
    </>
  );
}

// Problems — the student works the problem first. Hint and answer are hidden
// behind their own "Show hint" / "Show answer" toggles (mirrors the teacher
// editor's reveal, but also gates the hint) so nothing is spoiled up front.
function ProblemsSection({ sec, accent }) {
  const accent2 = useAccent2();
  const [revealed, setRevealed] = useState({}); // { [i]: { hint, answer } }
  const toggle = (i, key) => setRevealed(prev => ({ ...prev, [i]: { ...prev[i], [key]: !prev[i]?.[key] } }));
  return (
    <>
      {(sec.items || []).map((item, i) => {
        const dd = DIFF[item.difficulty] || DIFF.easy;
        const state = revealed[i] || {};
        return (
          <div key={i} className="rounded-2xl border overflow-hidden"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--st-border)' }}>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: dd.bg, color: dd.text }}>{dd.l}</span>
              <span className="text-xs font-semibold ml-auto" style={{ color: 'var(--st-mute)' }}>#{i + 1}</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-base font-semibold"><MathText text={item.question} /></p>

              {/* Reveal toggles — only offered for the parts that exist. */}
              <div className="flex flex-wrap gap-2">
                {item.hint && (
                  <button type="button" onClick={() => toggle(i, 'hint')}
                    data-testid={`problem-hint-toggle-${i}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
                    style={{ color: accent2, borderColor: `${accent2}33`, background: `${accent2}0d` }}>
                    {state.hint ? 'Hide hint' : 'Show hint'}
                  </button>
                )}
                {item.answer && (
                  <button type="button" onClick={() => toggle(i, 'answer')}
                    data-testid={`problem-answer-toggle-${i}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
                    style={{ color: accent, borderColor: `${accent}33`, background: `${accent}08` }}>
                    {state.answer ? 'Hide answer' : 'Show answer'}
                  </button>
                )}
              </div>

              {item.hint && state.hint && (
                <p className="text-sm px-3 py-2 rounded-lg border"
                  style={{ color: accent2, background: `${accent2}0d`, borderColor: `${accent2}26` }}>
                  Hint: <MathText text={item.hint} />
                </p>
              )}
              {item.answer && state.answer && (
                <div className="rounded-lg p-3 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
                  <p className="text-sm"><MathText text={item.answer} /></p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

// A resource with a teacher-added link is the same row, made clickable (opens
// in a new tab) — the link itself isn't shown.
function ListSection({ sec, accent }) {
  return (
    <>
      {(sec.items || []).map((item, i) => {
        const href = toSafeHref(item.url);
        const body = (
          <>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: `${accent}08`, borderColor: `${accent}15` }}>
              {item.type === 'video' ? <PlayCircle className="w-4 h-4" style={{ color: accent }} /> : <BookOpen className="w-4 h-4" style={{ color: accent }} />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold"><MathText text={item.title} /></p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--st-mute)' }}><MathText text={item.description} /></p>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>{item.type}</span>
          </>
        );
        const rowClass = 'flex items-center gap-4 p-4 rounded-xl border hover:shadow-md transition';
        const rowStyle = { borderColor: 'var(--st-border)', background: 'var(--st-card)' };
        return href ? (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" title={href}
            className={`${rowClass} cursor-pointer`} style={rowStyle} data-testid={`student-list-link-${sec.id}-${i}`}>
            {body}
          </a>
        ) : (
          <div key={i} className={rowClass} style={rowStyle}>{body}</div>
        );
      })}
    </>
  );
}

function BlackboardSection({ sec, accent }) {
  const accent2 = useAccent2();
  return (
    <div className="space-y-5">
      {sec.extracted_text && (
        <div className="rounded-2xl p-5 border relative overflow-hidden" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 w-1 h-full rounded-r" style={{ background: accent2 }} />
          <p className="text-xs font-semibold mb-3 ml-4" style={{ color: accent2 }}>Extracted content</p>
          <RenderContent text={sec.extracted_text} className="ml-4 text-base leading-[1.9]" />
        </div>
      )}
      {sec.explanation && (
        <div className="rounded-2xl p-5 border relative overflow-hidden" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 w-1 h-full rounded-r" style={{ background: accent }} />
          <p className="text-xs font-semibold mb-3 ml-4" style={{ color: accent }}>Digital explanation</p>
          <RenderContent text={sec.explanation} className="ml-4 text-base leading-[1.9]" />
        </div>
      )}
      {Array.isArray(sec.key_formulas) && sec.key_formulas.length > 0 && (
        <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <p className="text-xs font-semibold mb-3 text-indigo-500">Key Formulas</p>
          <div className="space-y-2">
            {sec.key_formulas.map((f, i) => (
              <div key={i} className="px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
                <p className="text-sm font-mono font-semibold"><MathText text={f} /></p>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(sec.step_by_step) && sec.step_by_step.length > 0 && (
        <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <p className="text-xs font-semibold mb-3 text-teal-600">Step-by-Step</p>
          <div className="space-y-3">
            {sec.step_by_step.map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold" style={{ background: `${accent}1f`, color: accent }}>{i + 1}</div>
                <p className="text-sm leading-relaxed pt-1"><MathText text={step} /></p>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(sec.concepts) && sec.concepts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sec.concepts.map((c, i) => (
            <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full border" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}><MathText text={c} /></span>
          ))}
        </div>
      )}
      {sec.diagram_description && (
        <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <p className="text-xs font-semibold mb-3 text-purple-500">Diagram Description</p>
          <p className="text-sm leading-relaxed"><MathText text={sec.diagram_description} /></p>
        </div>
      )}
    </div>
  );
}

// ─── Section-level decorators ─────────────────────────────────────────────
function SectionImages({ sec, onExpand, accent }) {
  const imgs = sec.section_images || [];
  if (!imgs.length) return null;
  return (
    <>
      {imgs.map((img, i) => {
        const isDrawing = img.caption === 'Drawing' || (img.url || '').startsWith('data:image/png');
        return (
          <div key={i} className="relative my-2" data-testid={`student-pv-secimg-${i}`}>
            {isDrawing ? (
              <div className="rounded-2xl border-2 overflow-hidden shadow-lg" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
                <div className="flex items-center gap-2.5 px-5 py-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}12` }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>
                  </div>
                  <p className="text-xs font-semibold " style={{ color: 'var(--st-mute)' }}>Diagram / Drawing</p>
                </div>
                <DrawingPreviewBody url={img.url} caption={img.caption || 'Drawing'} sMuted="" />
              </div>
            ) : (
              <button onClick={() => onExpand({ src: img.url })} className="block w-full text-left group">
                <img src={img.url} alt={img.caption || ''} className="w-full max-h-[550px] object-contain mx-auto rounded-xl border" style={{ borderColor: 'var(--st-border)' }} />
              </button>
            )}
            {img.caption && img.caption !== 'Drawing' && (
              <p className="text-xs mt-3 italic" style={{ color: 'var(--st-mute)' }}>{img.caption}</p>
            )}
          </div>
        );
      })}
    </>
  );
}

function SectionLinks({ sec, accent }) {
  const links = sec.section_links || [];
  if (!links.length) return null;
  return (
    <div className="mt-4 space-y-3" data-testid={`student-pv-links-${sec.id}`}>
      <p className="text-xs font-semibold flex items-center gap-2" style={{ color: accent }}>
        <Link2 className="w-3.5 h-3.5" /> Reference Links
      </p>
      <div className="space-y-2.5">
        {links.map((link, li) => {
          const href = toSafeHref(link.url);
          if (!href) return null;
          const isYT = link.link_type === 'youtube' && link.youtube_id;
          return isYT ? (
            <a key={li} href={href} target="_blank" rel="noopener noreferrer"
              className="block rounded-2xl overflow-hidden border hover:shadow-xl transition group/yt"
              style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
              data-testid={`student-pv-link-${sec.id}-${li}`}>
              <div className="relative">
                <img src={`https://img.youtube.com/vi/${link.youtube_id}/hqdefault.jpg`} alt="" className="w-full h-[200px] object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover/yt:bg-black/20 transition-colors pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover/yt:scale-110 transition-transform">
                    <PlayCircle className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
              <div className="px-5 py-3.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <PlayCircle className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{link.title || 'YouTube Video'}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--st-mute)' }}>youtube.com</p>
                </div>
              </div>
            </a>
          ) : (
            <a key={li} href={href} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 px-5 py-4 rounded-2xl border hover:shadow-lg transition group/lnk"
              style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
              data-testid={`student-pv-link-${sec.id}-${li}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border"
                style={{ background: `${accent}08`, borderColor: `${accent}15` }}>
                <Globe className="w-4 h-4" style={{ color: accent }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold group-hover/lnk:underline">{link.title || link.url}</p>
                <p className="text-xs truncate mt-0.5" style={{ color: 'var(--st-mute)' }}>{link.url}</p>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function SectionCharts({ sec, accent }) {
  const charts = sec.charts || [];
  if (!charts.length) return null;
  return (
    <div className="mt-5 space-y-5" data-testid={`student-pv-charts-${sec.id}`}>
      <p className="text-xs font-semibold flex items-center gap-2" style={{ color: accent }}>
        <BarChart3 className="w-3.5 h-3.5" /> Diagrams & Flowcharts
      </p>
      {charts.map((ch, ci) => (
        <div key={ch.id || ci} className="rounded-2xl border overflow-hidden shadow-sm"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
          data-testid={`student-pv-chart-${sec.id}-${ci}`}>
          <div className="px-5 py-3 border-b flex items-center gap-2.5" style={{ background: `${accent}10`, borderColor: `${accent}22` }}>
            <BarChart3 className="w-4 h-4" style={{ color: accent }} />
            <p className="text-sm font-semibold">{ch.title || `Flowchart ${ci + 1}`}</p>
            <span className="text-xs font-mono px-2 py-0.5 rounded ml-auto" style={{ background: `${accent}14`, color: accent }}>
              {(ch.nodes || []).length} shapes · {(ch.connectors || []).length} lines
            </span>
          </div>
          <ChartPreviewBody ch={ch} accent={accent} sBody="" sMuted="" isDark={false} />
        </div>
      ))}
    </div>
  );
}

function SectionAttachments({ sec, accent, onOpen }) {
  const atts = sec.attachments || [];
  if (!atts.length) return null;
  return (
    <div className="mt-5 space-y-3" data-testid={`student-pv-attachments-${sec.id}`}>
      <p className="text-xs font-semibold flex items-center gap-2" style={{ color: accent }}>
        <Paperclip className="w-3.5 h-3.5" /> Attachments
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {atts.map((att) => (
          <StudentAttachmentPreviewCard key={att.id} attachment={att} accent={accent} sectionId={sec.id}
            onOpen={() => onOpen(att)} />
        ))}
      </div>
    </div>
  );
}

// ─── Tables ──────────────────────────────────────────────────────────────
function LessonTable({ table, sectionId, idx }) {
  const C = resolveTableColors(table);
  const rows = table.rows || [];
  const headers = table.headers || (rows[0] || []);
  const body = table.headers ? rows : rows.slice(1);
  return (
    <div className="overflow-x-auto rounded-2xl border shadow-sm"
      style={{ borderColor: `${C.border}33`, background: C.odd_row_bg }}
      data-testid={`student-pv-table-${sectionId}-${idx}`}>
      {table.title && (
        <div className="px-5 py-3 border-b flex items-center gap-2.5" style={{ background: C.header_bg, borderColor: `${C.border}22` }}>
          <BarChart3 className="w-4 h-4" style={{ color: C.accent }} />
          <p className="text-sm font-semibold" style={{ color: C.header_text }}>{table.title}</p>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: C.header_bg }}>
            {headers.map((h, j) => (
              <th key={j} className="text-left px-5 py-3.5 font-semibold text-xs border-b" style={{ color: C.header_text, borderColor: `${C.border}22` }}><MathText text={h} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, j) => (
            <tr key={j} className="border-b last:border-0" style={{ background: j % 2 === 1 ? C.even_row_bg : C.odd_row_bg, borderColor: `${C.border}14` }}>
              {r.map((c, k) => (
                <td key={k} className={`px-5 py-3 ${k === 0 ? 'font-medium' : ''}`} style={{ color: C.text }}><MathText text={c} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Fallback / References / Lightbox ────────────────────────────────────
function LessonFallback({ content }) {
  if (!content) {
    return (
      <div className="rounded-2xl border-2 border-dashed p-8 text-center text-sm"
        style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>
        This lesson has no written content yet. Try asking Deskey Tutor on the right pane.
      </div>
    );
  }
  return <div className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--st-ink)' }}>{content}</div>;
}

export function LessonReferences({ references, accent }) {
  // Uploaded photo references are base64 data: URLs, which browsers refuse
  // to open in a new tab — show them in the in-page lightbox instead.
  const [viewing, setViewing] = useState(null);
  if (!references || !references.length) return null;
  return (
    <div className="mt-12 pt-6 border-t" style={{ borderColor: 'var(--st-border)' }} data-testid="lesson-references-list">
      <p className="text-xs font-semibold mb-4 flex items-center gap-2" style={{ color: accent || 'var(--st-mute)' }}>
        <Link2 className="w-3.5 h-3.5" /> References
      </p>
      <ol className="space-y-2">
        {references.map((r, i) => {
          const url = r?.url || '';
          const isInline = isDataUrl(url);
          const isImage = isViewableInlineImage(url);
          const isLink = isOpenableLink(url);
          const title = r?.title || (isInline ? 'Image' : url) || 'Untitled';
          const thumbnail = isImage ? url : r?.thumbnail;
          const inner = (
            <span className="flex items-start gap-3">
              <span className="text-xs font-mono font-semibold mt-1 px-1.5 py-0.5 rounded" style={{ background: `${accent || '#0d9488'}14`, color: accent || '#0d9488' }}>[{i + 1}]</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold" style={{ color: 'var(--st-ink)' }}>{title}</span>
                {url && !isInline && <span className="block text-xs truncate mt-0.5" style={{ color: 'var(--st-mute)' }}>{url}</span>}
                {thumbnail && (
                  <img src={thumbnail} alt="" className="mt-2 max-h-24 rounded border" style={{ borderColor: 'var(--st-border)' }} />
                )}
              </span>
              {r?.type && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full border self-start mt-1" style={{ borderColor: 'var(--st-border)', color: 'var(--st-mute)' }}>{r.type}</span>
              )}
            </span>
          );
          if (isImage) {
            return (
              <li key={i} data-testid={`lesson-reference-${i}`}>
                <button type="button" onClick={() => setViewing({ src: url, title })}
                  className="block w-full text-left px-4 py-3 rounded-xl border hover:shadow-md transition cursor-zoom-in"
                  style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
                  data-testid={`lesson-reference-open-${i}`}>{inner}</button>
              </li>
            );
          }
          return isLink ? (
            <li key={i}>
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="block px-4 py-3 rounded-xl border hover:shadow-md transition"
                style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
                data-testid={`lesson-reference-${i}`}>{inner}</a>
            </li>
          ) : (
            <li key={i} className="block px-4 py-3 rounded-xl border" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid={`lesson-reference-${i}`}>
              {inner}
            </li>
          );
        })}
      </ol>
      {viewing && (
        <PreviewLightbox src={viewing.src} alt={viewing.title} caption={viewing.title} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Suggested exam questions — the PYQs exam_lesson_tagging.py's background
// vector-search job matched to this lesson, for the student's own target
// exam. Below everything else (after References), collapsed by default;
// fetched lazily on first expand rather than on every lesson load.
// ═══════════════════════════════════════════════════════════════════════════
const EXAM_LABELS = { JEE_MAIN: 'JEE Main', JEE_ADVANCED: 'JEE Advanced', NEET: 'NEET' };

function SuggestedExamQuestions({ publishedLessonId }) {
  // `|| {}`: LessonView is also rendered outside the student app (teacher
  // "Student view" preview, admin Teacher Desk) where there is no student
  // context — without a student there's no target exam, so this hides itself.
  const { me, theme } = useStudent() || {};
  const examPrep = getExamPrepColors(theme?.id);
  const targetExam = me?.student_profile?.target_exam || '';
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // null = not fetched yet
  const [loading, setLoading] = useState(false);

  if (!targetExam || !publishedLessonId) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && state === null) {
      setLoading(true);
      api.get(`/student/exam-prep/lessons/${publishedLessonId}/suggested-questions`)
        .then(setState)
        .catch(() => setState({ status: 'none', exam: targetExam, questions: [] }))
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="mt-12 pt-6 border-t" style={{ borderColor: 'var(--st-border)' }} data-testid="suggested-exam-questions">
      <button onClick={toggle} data-testid="suggested-exam-questions-toggle"
        className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-xs font-semibold flex items-center gap-2" style={{ color: examPrep.gold }}>
          <GraduationCap className="w-3.5 h-3.5" /> Suggested {EXAM_LABELS[targetExam] || targetExam} Questions
        </span>
        <ChevronDown className="w-4 h-4 transition-transform" style={{ color: examPrep.gold, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div className="mt-4">
          {loading && (
            <Loader label="Loading suggested questions…" />
          )}
          {!loading && state && (state.status !== 'done' || state.questions.length === 0) && (
            <p className="text-xs py-2" style={{ color: 'var(--st-mute)' }}>
              {state.status === 'pending'
                ? 'Still finding relevant questions for this lesson — check back shortly.'
                : state.status === 'not_in_syllabus'
                  ? `This lesson's topic isn't part of the ${EXAM_LABELS[targetExam] || targetExam} syllabus, so there are no ${EXAM_LABELS[targetExam] || targetExam} questions for it.`
                  : 'No suggested questions for this lesson yet.'}
            </p>
          )}
          {!loading && state?.status === 'done' && state.questions.length > 0 && (
            <div className="space-y-2.5">
              {state.questions.map(q => (
                <SuggestedQuestionCard key={q.id} question={q} examPrep={examPrep} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestedQuestionCard({ question, examPrep }) {
  const [expanded, setExpanded] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [loadingAnswer, setLoadingAnswer] = useState(false);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && answer === null) {
      setLoadingAnswer(true);
      api.get(`/student/exam-prep/questions/${question.id}/answer`)
        .then(setAnswer)
        .catch(() => setAnswer({ correct_answer: null, solution_latex: '' }))
        .finally(() => setLoadingAnswer(false));
    }
  };

  return (
    // min-w-0 + overflow-x-auto on each text block: KaTeX inline math never
    // wraps, so a long piecewise/limit expression would otherwise run past
    // the card edge -- it scrolls inside its own block instead.
    <div className="rounded-xl border p-3.5 min-w-0" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid={`suggested-question-${question.id}`}>
      <button onClick={toggleExpand} className="w-full text-left min-w-0">
        <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold opacity-60">
          <span>{question.year}</span><span>&middot;</span><span className="capitalize">{question.difficulty}</span>
          <span>&middot;</span><span className="capitalize">{(question.question_type || '').replace('_', '-')}</span>
        </div>
        <div className="text-sm overflow-x-auto break-words" style={{ color: 'var(--st-ink)' }}>
          <MathText text={question.question_latex} />
        </div>
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--st-border)' }}>
          {loadingAnswer && (
            <div className="text-xs opacity-60 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading answer…
            </div>
          )}
          {!loadingAnswer && answer && (
            <>
              {question.question_type === 'numerical' ? (
                <div className="text-xs font-semibold" style={{ color: examPrep.gold }}>Answer: {answer.correct_answer}</div>
              ) : (
                <div className="space-y-1.5">
                  {(question.options_latex || []).map((opt, i) => {
                    const isCorrect = 'ABCD'.indexOf(answer.correct_answer) === i;
                    return (
                      <div key={i} className="px-3 py-2 rounded-lg border text-xs overflow-x-auto break-words"
                        style={{ borderColor: isCorrect ? '#16A34A' : 'var(--st-border)', background: isCorrect ? 'color-mix(in srgb, #16A34A 12%, transparent)' : 'transparent', color: 'var(--st-ink)' }}>
                        <MathText text={opt} />
                      </div>
                    );
                  })}
                </div>
              )}
              {answer.solution_latex && (
                <div className="mt-2.5 p-3 rounded-lg text-xs overflow-x-auto break-words" style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>
                  <strong style={{ color: 'var(--st-ink)' }}>Solution:</strong> <MathText text={answer.solution_latex} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Lightbox({ src, onClose }) {
  // Portalled to document.body — the page's <main> establishes its own
  // stacking context (position:relative + z-index), which traps a
  // regularly-nested fixed element below the app header no matter how high
  // its own z-index goes. Escaping via portal renders it as a true sibling
  // of the header, so z-[70] actually wins.
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="lesson-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30" data-testid="lesson-lightbox-close">
        <X className="w-5 h-5" />
      </button>
      <img src={src} alt="" className="max-w-[92vw] max-h-[90vh] rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} />
    </div>,
    document.body
  );
}
